/* ── Web motion: the workspace and its panels ────────────────────────────
   Web only. Imported right after global.css by src/app/_layout.web.tsx.

   Layout changes in the three-pane workspace run as View Transitions
   (components/layout/workspace-transition.ts; model in lib/web-motion.ts).
   While one runs, <html data-tabbio-vt="<kind>"> is set and the three panes
   carry a view-transition-name, so the browser morphs them between the old
   and the new layout:

   - panel-open, artifact-open   the page (or the file: document, slides,
                                 image) slides in from the end edge and fades
                                 up, while the chat column narrows.
   - panel-close, artifact-close the same in reverse, a little quicker.
   - artifact-swap, -resize,     the boxes morph; their contents keep their
     layout                      size instead of stretching.

   The conversation itself never cross-fades (two copies of a thread at
   different widths read as ghosted text): it fades through. The old column
   goes quickly, then the new one settles in where it will stay. Panels that
   change content in place (swap, resize) fade through the same way.

   A page change inside an already open panel plays a short enter animation
   from JS (playPanelContentEnter). Reduced motion never reaches any of this:
   the shell renders the new layout directly, and the CSS-only entrances at
   the end sit behind prefers-reduced-motion: no-preference. */

:root {
  --tabbio-motion-layout: 360ms;
  --tabbio-motion-exit: 240ms;
  --tabbio-motion-swap: 220ms;
  /* Fade-through: the old content leaves in this, the new one then enters. */
  --tabbio-motion-fade-out: 110ms;
  --tabbio-motion-fade-in: 260ms;
  --tabbio-ease-standard: cubic-bezier(0.2, 0, 0, 1);
  --tabbio-ease-enter: cubic-bezier(0.16, 1, 0.3, 1);
  --tabbio-ease-exit: cubic-bezier(0.3, 0, 0.8, 0.15);
  /* How far a panel travels as it enters or leaves, toward the end edge. */
  --tabbio-motion-shift: 40px;
  /* The new-chat cloud, timed like Gemini's: it grows in once, then the
     cloud breathes while its pale bloom drifts and tilts on unrelated periods
     (each runs there and back), so the pattern never visibly repeats.
     Ambient light, never motion to watch. */
  --tabbio-motion-cloud-grow: 1s;
  --tabbio-motion-cloud-breathe: 9.5s;
  --tabbio-motion-cloud-drift: 7.3s;
  --tabbio-motion-cloud-tilt: 11.7s;
}
html[dir="rtl"] {
  --tabbio-motion-shift: -40px;
}

/* Names exist only during a transition. A view-transition-name makes its
   element a stacking context, which must not leak into everyday layout. */
html[data-tabbio-vt] .tabbio-chat-column {
  view-transition-name: tabbio-chat;
}
html[data-tabbio-vt] .tabbio-content-panel {
  view-transition-name: tabbio-panel;
}
html[data-tabbio-vt] .tabbio-artifact-slot {
  view-transition-name: tabbio-artifact;
}

/* Everything else (sidebar, headers, overlays) swaps at once. */
html[data-tabbio-vt]::view-transition-old(root),
html[data-tabbio-vt]::view-transition-new(root) {
  animation: none;
}

html[data-tabbio-vt]::view-transition-group(*) {
  animation-duration: var(--tabbio-motion-layout);
  animation-timing-function: var(--tabbio-ease-standard);
}

/* Boxes resize; their pictures keep their natural size and are clipped by
   the moving box, so text never stretches mid-morph. */
html[data-tabbio-vt]::view-transition-old(tabbio-chat),
html[data-tabbio-vt]::view-transition-new(tabbio-chat),
html[data-tabbio-vt]::view-transition-old(tabbio-panel),
html[data-tabbio-vt]::view-transition-new(tabbio-panel),
html[data-tabbio-vt]::view-transition-old(tabbio-artifact),
html[data-tabbio-vt]::view-transition-new(tabbio-artifact) {
  width: 100%;
  height: 100%;
  object-fit: none;
  object-position: left top;
  overflow: clip;
}
/* Pinned to the start edge, which no pane moves: the chat column only
   changes width at its end edge, so each picture stays exactly where its
   layout puts it and nothing drifts sideways. */
html[dir="rtl"][data-tabbio-vt]::view-transition-old(tabbio-chat),
html[dir="rtl"][data-tabbio-vt]::view-transition-new(tabbio-chat),
html[dir="rtl"][data-tabbio-vt]::view-transition-old(tabbio-panel),
html[dir="rtl"][data-tabbio-vt]::view-transition-new(tabbio-panel),
html[dir="rtl"][data-tabbio-vt]::view-transition-old(tabbio-artifact),
html[dir="rtl"][data-tabbio-vt]::view-transition-new(tabbio-artifact) {
  object-position: right top;
}

/* Fade-through for content that changes in place: out fast, then the new
   content settles in (a short rise, no sideways travel). */
@keyframes tabbio-vt-content-out {
  to {
    opacity: 0;
  }
}
@keyframes tabbio-vt-content-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
}
html[data-tabbio-vt]::view-transition-old(tabbio-chat),
html[data-tabbio-vt]::view-transition-old(tabbio-panel):not(:only-child),
html[data-tabbio-vt]::view-transition-old(tabbio-artifact):not(:only-child) {
  animation: tabbio-vt-content-out var(--tabbio-motion-fade-out) var(--tabbio-ease-exit) both;
}
html[data-tabbio-vt]::view-transition-new(tabbio-chat),
html[data-tabbio-vt]::view-transition-new(tabbio-panel):not(:only-child),
html[data-tabbio-vt]::view-transition-new(tabbio-artifact):not(:only-child) {
  animation: tabbio-vt-content-in var(--tabbio-motion-fade-in) var(--tabbio-ease-enter)
    var(--tabbio-motion-fade-out) both;
}

/* A panel that appears slides in from the end edge; one that goes slides
   back out the same way. */
@keyframes tabbio-vt-panel-enter {
  from {
    opacity: 0;
    transform: translateX(var(--tabbio-motion-shift));
  }
}
@keyframes tabbio-vt-panel-exit {
  to {
    opacity: 0;
    transform: translateX(var(--tabbio-motion-shift));
  }
}
html[data-tabbio-vt]::view-transition-new(tabbio-panel):only-child,
html[data-tabbio-vt]::view-transition-new(tabbio-artifact):only-child {
  animation: tabbio-vt-panel-enter var(--tabbio-motion-layout) var(--tabbio-ease-enter) both;
}
html[data-tabbio-vt]::view-transition-old(tabbio-panel):only-child,
html[data-tabbio-vt]::view-transition-old(tabbio-artifact):only-child {
  animation: tabbio-vt-panel-exit var(--tabbio-motion-exit) var(--tabbio-ease-exit) both;
}

/* Opening a different file over an open one is quicker than a layout
   change: the box barely moves, only the file fades through. */
html[data-tabbio-vt="artifact-swap"]::view-transition-group(tabbio-artifact) {
  animation-duration: var(--tabbio-motion-swap);
}

/* ── CSS-only entrances ───────────────────────────────────────────────── */
@media (prefers-reduced-motion: no-preference) {
  /* The image lightbox: the picture settles in as the backdrop fades. */
  .tabbio-lightbox-figure {
    animation: tabbio-lightbox-figure-in 240ms var(--tabbio-ease-enter) both;
  }
}
@keyframes tabbio-lightbox-figure-in {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
}

/* ── Ambient: the new-chat cloud ──────────────────────────────────────────
   Gemini's new-chat glow grows in and lives; Tabbio's does too (owner
   requests, 2026-10-11 and 2026-10-12). The cloud (global.css,
   .tabbio-hero-aura) grows out of the composer once, as Gemini's does. Then
   the cloud (::before) breathes while the pale bloom (::after) drifts and
   tilts across it, so the paler pocket around the composer slowly shifts.
   Each animation owns one transform property, so none overrides another,
   and all of them stay on the compositor: the masks are painted once.
   Reduced motion shows the still cloud straight away. */
@media (prefers-reduced-motion: no-preference) {
  .tabbio-workspace.is-chat-only:not(.is-artifact-expanded) .tabbio-hero-aura {
    transform-origin: 50% calc(50% + 15px);
    animation: tabbio-hero-cloud-grow var(--tabbio-motion-cloud-grow) var(--tabbio-ease-standard) both;
  }
  .tabbio-workspace.is-chat-only:not(.is-artifact-expanded) .tabbio-hero-aura::before {
    animation: tabbio-hero-cloud-breathe var(--tabbio-motion-cloud-breathe) ease-in-out infinite alternate;
  }
  .tabbio-workspace.is-chat-only:not(.is-artifact-expanded) .tabbio-hero-aura::after {
    animation:
      tabbio-hero-cloud-drift var(--tabbio-motion-cloud-drift) ease-in-out infinite alternate,
      tabbio-hero-cloud-tilt var(--tabbio-motion-cloud-tilt) ease-in-out infinite alternate;
  }
}
@keyframes tabbio-hero-cloud-grow {
  from {
    opacity: 0;
    scale: 0;
  }
  to {
    opacity: 1;
    scale: 1;
  }
}
@keyframes tabbio-hero-cloud-breathe {
  from {
    scale: 1;
  }
  to {
    scale: 1.05;
  }
}
@keyframes tabbio-hero-cloud-drift {
  from {
    translate: -3% 1%;
  }
  to {
    translate: 3% -1%;
  }
}
@keyframes tabbio-hero-cloud-tilt {
  from {
    rotate: -2deg;
  }
  to {
    rotate: 2deg;
  }
}
